<script setup lang="ts">
import { Link, useForm } from "@inertiajs/vue3"
import { UiButton, UiCheckbox, UiFormLayout, UiSelect, UiTextInput } from "@/components"
import { useNotifications } from "@/composables/useNotifications"

type TaxProfilePayload = {
  id?: number | null
  company_id: number | ""
  name: string
  active: boolean
  default: boolean
  credit_icms: boolean
  credit_pis_cofins: boolean
  ipi_included_in_cost: boolean
  estimated_sales_tax_percent: string
}

const props = defineProps<{
  mode: "create" | "edit"
  title: string
  subtitle?: string
  tax_profile: TaxProfilePayload
  companies: Array<{ id: number; name: string }>
  errors?: Record<string, string[]>
}>()

const form = useForm({
  tax_profile: {
    company_id: props.tax_profile.company_id,
    name: props.tax_profile.name,
    active: props.tax_profile.active,
    default: props.tax_profile.default,
    credit_icms: props.tax_profile.credit_icms,
    credit_pis_cofins: props.tax_profile.credit_pis_cofins,
    ipi_included_in_cost: props.tax_profile.ipi_included_in_cost,
    estimated_sales_tax_percent: props.tax_profile.estimated_sales_tax_percent,
  },
})

const notify = useNotifications()
const fieldError = (field: string) => props.errors?.[field]?.[0]

function submit() {
  const opts = {
    onSuccess: () =>
      notify.success(
        props.mode === "create"
          ? "Perfil fiscal criado com sucesso"
          : "Perfil fiscal atualizado com sucesso"
      ),
  }
  if (props.mode === "create") {
    form.post("/tax_profiles", opts)
    return
  }

  form.put(`/tax_profiles/${props.tax_profile.id}`, opts)
}
</script>

<template>
  <UiFormLayout :title="title" :subtitle="subtitle" back-href="/tax_profiles" back-label="Voltar para perfis">
    <form class="space-y-4" @submit.prevent="submit">
      <div class="grid gap-4 md:grid-cols-2">
        <UiSelect
          id="profile-company"
          :model-value="form.tax_profile.company_id"
          label="Empresa"
          :options="companies.map((company) => ({ label: company.name, value: company.id }))"
          :disabled="mode === 'edit'"
          :error="fieldError('company') || fieldError('company_id')"
          @update:model-value="form.tax_profile.company_id = $event as number | ''"
        />

        <UiTextInput
          id="profile-name"
          v-model="form.tax_profile.name"
          label="Nome do perfil"
          placeholder="Ex: Padrão Brasil"
          :error="fieldError('name')"
        />

        <UiTextInput
          id="profile-sales-tax"
          v-model="form.tax_profile.estimated_sales_tax_percent"
          type="number"
          label="Imposto estimado venda (%)"
          placeholder="15.5"
          step="0.01"
          min="0"
          :error="fieldError('estimated_sales_tax_percent')"
        />
      </div>

      <div class="space-y-3 border-t border-slate-200 pt-4">
        <h3 class="text-sm font-medium text-slate-900">Politica de creditos e custos</h3>

        <div class="space-y-2">
          <UiCheckbox
            id="profile-credit-icms"
            v-model="form.tax_profile.credit_icms"
            label="Aproveitar credito de ICMS na entrada"
          />

          <UiCheckbox
            id="profile-credit-pis-cofins"
            v-model="form.tax_profile.credit_pis_cofins"
            label="Aproveitar credito de PIS/COFINS na entrada"
          />

          <UiCheckbox
            id="profile-ipi-cost"
            v-model="form.tax_profile.ipi_included_in_cost"
            label="Incluir IPI no custo do produto"
          />

          <UiCheckbox id="profile-active" v-model="form.tax_profile.active" label="Perfil ativo" />

          <UiCheckbox id="profile-default" v-model="form.tax_profile.default" label="Perfil padrao da empresa" />
        </div>
      </div>

      <div class="flex items-center gap-3 pt-2">
        <UiButton type="submit" :disabled="form.processing">
          {{ mode === "create" ? "Criar perfil" : "Salvar alteracoes" }}
        </UiButton>

        <Link href="/tax_profiles" class="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100">
          Cancelar
        </Link>
      </div>
    </form>
  </UiFormLayout>
</template>
